<template lang="pug">
div
  h1.u-mb-sm {{$t('mantra')}}

  .grid.is-tablet-10.is-widescreen-8.is-desktop-9
    .column
      p.u-mb-xs {{$t('mantraDesc1')}}
      p.u-mb-xs {{$t('mantraDesc2')}}
      p {{$t('mantraDesc3')}}

  .u-mt-md
    h2.u-mb-xm {{$t('digitalExperience')}}

    .grid.is-tablet-10.is-widescreen-8.is-desktop-9
      .column
        p.u-mb-xs(v-for="item in $t('digitalExperienceItems')") {{item}}
  .u-mt-md
    h2.u-mb-mi {{$t('disabilities')}}

    .grid.is-widescreen-6
      .column.u-mb-sm
        h4 {{$t('visual')}}
        p.u-mb-xs(v-for="item in $t('visualItems')") {{item}}
      .column.u-mb-sm
        h4 {{$t('auditory')}}
        p.u-mb-xs(v-for="item in $t('auditoryItems')") {{item}}
      .column.u-mb-sm
        h4 {{$t('speech')}}
        p.u-mb-xs(v-for="item in $t('speechItems')") {{item}}

      .column.u-mb-sm
        h4 {{$t('physical')}}
        p.u-mb-xs(v-for="item in $t('physicalItems')") {{item}}


  .u-mt-md
    h2.u-mb-xm {{$t('accessibility')}}
    .grid.is-tablet-10.is-widescreen-8.is-desktop-9
      .column
        .u-mb-xs
          AppLink(to="https://www.w3.org/TR/WCAG21/" class="button-link" :title="$t('accessibilityWeb')") {{$t('accessibilityWeb')}}
        .u-mb-xs
          AppLink(to="https://www.w3.org/WAI/" class="button-link" :title="$t('accessibilityWebStrategies')") {{$t('accessibilityWebStrategies')}}




  .u-mt-md
    h2.u-mb-xm {{$t('resourcesTools')}}
    .grid.is-tablet-10.is-widescreen-8.is-desktop-9
      .column
        .u-mb-xs
          AppLink(to="https://www.w3.org/TR/low-vision-needs/" class="button-link" :title="$t('w3c')") {{$t('w3c')}}
        .u-mb-xs
          AppLink(to="/tools/contrast" class="button-link" :title="$t('contrast')") {{$t('contrast')}}




</template>

<script>
import { generateSeoMeta } from '~/utils/seo';

export default {
  scrollToTop: true,
  layout: 'base',
  head: generateSeoMeta({ title: 'pageTitle', description: 'pageDesc' }, { i18n: true })
};
</script>

<i18n>
{
  "en": {
    "pageTitle":"Accessibility",
    "pageDesc":"Accessibility",
    "mantra":"Accessibility matters",
    "mantraDesc1":"Humanizing the web for different individuals around the world is a fair target to set. That's why the Web Content Acessibility Guidelines (WCAG) has a clear goal of providing a standard that defines the optimal level of accessibility for web content.",
    "mantraDesc2":"Accessiblity matters because it assures that everyone, including people with disabilites are allowed to get the best out of the web, and makes sure that every element on a web page is acessible and easy to use.",
    "mantraDesc3":"We, at Baianat, are on a quest to remove any barriers and ensure proper digital accessibility, so everyone can interact normally with the web content we provide, as well as contribute to it.",
    "digitalExperience":"Unified digital experience",
    "digitalExperienceItems":{
      "1":"There are different reasons why some people may have troubles navigating the web. It's not exclusive to people with disabilities, but it includes anyone who might be put into a non-optimal situation for browsing the web.",
      "2":"There are mainly three different categories of situtations which might hinder users from properly using the web if it's not made with accessibility in mind; the three categories are: permanent, temporary, or situational.",
      "3":"To demonstrate that accessibility is not just meant for people with disabilities, the situational category highlights certain situations that anyone can find himself in during his everyday life.",
      "4":"For example, captions are an excellent solution for people who want to go through some audible content in a situation where they do not want to disturb people around them. Although captions are originally meant for people with a hearing impairment or dyslexia, you can see that they can still be used situationally for anyone to provide better access to web content.",
      "5":"Another example that highlights a different form of disability is a person who broke a wrist or arm and is not able to use both the keyboard and mouse at the same time, so they rely on keyboard navigation for doing the most of their web navigation. This one falls into the temporary disability category, where users have limited access to browse the web."
    },
    "disabilities":"Different types of disabilities",
    "visual":"Visual",
    "visualItems":{
      "1":"There are different degrees of visual disabilities. They can range from a mild, or moderate vision loss in one or both eyes, to a substantial or complete loss of vision in both eyes. They can also be related to problems in interpreting colors, which relate to color blindness, and there's also the problem of being too sensitive to bright colors and brightness in general.",
      "2":"To propery categorize visual disabilities, we can list them as follows:",
      "3":"- Blindness: marginal or complete loss of vision in one or both eyes.",
      "4":"- Low vision: this refers to having a proper vision, like having a blurry or clouded vision, or seeing only certain parts of the visual field",
      "5":"- Color blindness: the difficulty to distinguish between different colors, or the inability to interpret any color"
    },
    "auditory":"Auditory",
    "auditoryItems":{
      "1":"This includes the different cases of having a specific hearing impairment in one or both ears. Users trying to access audio content can feel restricted if they have a partial loss in hearing.",
      "2":"The auditory disabilities include:",
      "3":"- Hearing difficulties: mild or moderate hearing impairment in one or both ears.",
      "4":"- Complete loss of hearing: A permanent and complete impairment in one or both ears."
    },
    "speech":"Speech",
    "speechItems":{
      "1":"People with certain speech problems produce speech that is difficult to recognize by people or software.",
      "2":"Speech didabilities include:",
      "3":"- Stuttering: talking with continued involuntary repetition of sounds, especially initial consonants.",
      "4":"- Dysarthria: difficult or unclear articulation of speech that is otherwise linguistically normal.",
      "5":"- Muteness: inability to speak, often caused by a speech disorder or surgery."
    },
    "physical":"Physical",
    "physicalItems":{
      "1":"Physical disabilities reflect motor problems or limitations of muscular control. They limit how someone could interact with someone or something through dynamic movements.",
      "2":"They include:",
      "3":"- Amputation: the removal of a limb or more by trauma, medical illness, or surgery.",
      "4":"- Arthritis: disorders that affect the the joints.",
      "5":"- Paralysis: the loss of muscle function for one or more muscles.",
      "6":"- Repetitive strain injuries: injuries that affect muscles, nerves, and tendons due to repeititive movement and overuse."
    },
    "accessibility":"Global Accessibility Standards",
    "accessibilityWeb":"Web Content Accessibility Guidelines",
    "accessibilityWebStrategies":"Strategies, standards, resources to make the Web accessible to people with disabilities",
    "resourcesTools":"Resources and Tools",
    "w3c":"W3C low vision requirements",
    "contrast":"Baianat contrast ratio tool"
  },
  "ar": {
    "pageTitle":"إمكانية الوصول",
    "pageDesc":"إمكانية الوصول",
    "mantra":"أهمية إمكانية الوصول",
    "mantraDesc1":"عملية تحسين الموقع ليكون أكثر آدمية للمستخدمين المختلفين في جميع أنحاء العالم وفقًا لاختلافاتهم، يعد هدفًا بديهيًا من الواجب تحقيقه. لهذا إرشادات إتاحة محتوى الويب (wcag) لها هدف واضح يتمثل في توفير معيار يحدد المستوى الأمثل لإمكانية الوصول لمحتوى الويب.",
    "mantraDesc2":"تعد مسألة إمكانية الوصول أمرًا مهمًا؛ لأنه يضمن لجميع المستخدمين بمن فيهم الذين يعانون من إعاقة أو مشكلة ما من الحصول على أفضل النتائج على الموقع، والتأكد من أن كل عنصر من عناصر صفحة الموقع سهلة الاستخدام والوصول.",
    "mantraDesc3":"في بيانات نسعى لإزالة أي حواجز ونضمن إمكانية الوصول؛ ليتمكن الجميع من التفاعل بشكل طبيعي مع محتوى الموقع الذي نقدمه، وكذلك المساهمة فيه.",
    "digitalExperience":"تجربة رقمية موحدة",
    "digitalExperienceItems":{
      "1":"هناك أسباب كثيرة تجعل بعض الأشخاص يواجهون مشكلة أثناء تصفح الموقع، ولا تقتصر هذه المشكلة على الأشخاص الذين يعانون من إعاقة ما بل يشمل الأمر جميع المستخدمين الذين يواجهون تجربة تصفح غير مثالية.",
      "2":"توجد ثلاث فئات رئيسية مختلفة من عمليات التخصيص التي تعيق المستخدمين من استخدام الموقع بشكل صحيح ويجب وضعها في عين الاعتبار، وهي دائمة، و مؤقتة، وظرفية.",
      "3":"لتوضيح أن إمكانية الوصول ليست مقتصرة على الأشخاص الذين يعانون من إعاقة ما، فإن الفئة الظرفية تسلط الضوء على بعض المواقف التى يمكن لأي شخص أن يتعرض لها في حياته اليومية.",
      "4":"علي سبيل المثال، تعتبر التعليقات المكتوبة حلًا ممتازًا للأشخاص الذين يرغبون في الاستمتاع بمحتوى مسموع بحيث لا يزعجون من حولهم. بالرغم من أن التعليقات المكتوبة مخصصة في الأساس للأشخاص الذين يعانون من ضعف السمع أو عسر القراءة. لكنك بالتأكيد تلاحظ أنه لا يزال من الممكن استخدامها ظاهريًا لأي شخص لتوفير تجربة أفضل للوصول إلى محتوى الويب.",
      "5":"مثال آخر يسلط الضوء على الإعاقة التي قد يعاني منها أى من المستخدمين، وهو الشخص الذي كسر معصم أو ذراع له ولا يستطيع استخدام لوحة المفاتيح أو الماوس في نفس الوقت. لهذا تجدهم يعتمدون على لوحة المفاتيح في عملية التنقل عبر الويب. تقع هذه الفئة ضمن فئة الإعاقة المؤقتة حيث يكون للمستخدمين إمكانية تصفح محدود للويب."
    },
    "disabilities":"أنواع مختلفة من الإعاقات",
    "visual":"البصرية",
    "visualItems":{
      "1":"الإعاقة البصرية لها درجات مختلفة. يمكن أن تتراوح ما بين فقدان البصر في إحدى العينين أو فقدان تام في كليهما. أو يمكن أن تكون ذات صلة بمشاكل تفسير الألوان، المرتبطة بعمى الألوان. وهناك أيضًا مشكلة الحساسية المفرطة من الألوان الساطعة والسطوع بشكل عام.",
      "2":"تُصنف الإعاقات البصرية بشكل صحيح، على النحو التالي:",
      "3":"- العمى: فقدان هامشي أو كامل للرؤية في واحدة أو كلتا العينين.",
      "4":"- ضعف الرؤية: يشير هذا إلى وجود رؤية مناسبة، مثل وجود رؤية ضبابية أو غامضة، أو رؤية أجزاء معينة فقط من المجال البصري.",
      "5":"- عمى الألوان: صعوبة التمييز بين الألوان المختلفة، أو عدم القدرة على تفسير أي لون"
    },
    "auditory":"التخاطب",
    "auditoryItems":{
      "1":"الأشخاص الذين لديهم مشاكل في الكلام، والذي ينتجون كلامًا صعب التعرف عليه سواء من ناحية الأشخاص أو البرامج.",
      "2":"تتضمن مشكلات التخاطب ما يلي:",
      "3":"- التلعثم: الحديث مع التكرار اللاإرادي للأصوات، وخاصة الحروف الساكنة الأولية.",
      "4":"- عسر التلفظ: صعوبة التعبير أو الأجزاء غير الواضحة من الكلام."
    },
    "speech":"السمعية",
    "speechItems":{
      "1":"وهذا يشمل الحالات المختلفة لوجود ضعف في السمع في واحدة أو كلتا الأذنين. يمكن للمستخدمين الذين يحاولون الوصول إلى المحتوى الصوتي أن يشعروا بالقيود إذا كان لديهم فقدان حتى لو جزئي في السمع.",
      "2":"تشمل الإعاقات السمعية ما يلي:",
      "3":"- صعوبة السمع: ضعف السمع الخفيف أو المتوسط في واحدة أو كلتا الأذنين.",
      "4":"- فقدان السمع التام: ضعف دائم وكامل في واحدة أو كلتا الأذنين.",
      "5":"- الخرس: عدم القدرة على الكلام، غالبًا ما ينتج عن اضطراب الكلام أو الجراحة."
    },
    "physical":"العضوية",
    "physicalItems":{
      "1":"تعكس الإعاقات البدنية أو العضوية مشاكل الحركة أو قيود التحكم العضلي. فهي تحد من كيفية تفاعل شخص ما مع شخص أو شيء ما من خلال الحركات الديناميكية.",
      "2":"وتشمل:",
      "3":"- البتر: إزالة أحد الأطراف أو أكثر عن طريق الصدمة أو المرض الطبي أو الجراحة.",
      "4":"- التهاب المفاصل: اضطرابات تؤثر على المفاصل.",
      "5":"- الشلل: فقدان وظيفة العضلات لعضلة واحدة أو أكثر.",
      "6":"- إصابات الإجهاد المتكررة: الإصابات التي تؤثر على العضلات والأعصاب والأوتار بسبب الحركة المتكررة والإفراط في الاستخدام."
    },
    "accessibility":"معايير إمكانية الوصول العالمية",
    "accessibilityWeb":"إرشادات إتاحة محتوى الويب",
    "accessibilityWebStrategies":"الاستراتيجيات والمعايير والمصادر لجعل الويب في متناول الأشخاص ذوي الإعاقات",
    "resourcesTools":"المصادر والأدوات",
    "w3c":"W3C متطلبات الرؤية المنخفضة",
    "contrast":"أداة كونتراست الخاصة ببيانات."
  }
}
</i18n>
